import api from '../api'
import {
  uploadImageRequest,
  uploadImageSuccess,
  uploadImageError,
  deleteImageRequest,
  deleteImageSuccess,
  deleteImageError
} from './image'
import { setFullscreenLoaderMessage } from './form'
import { validateDiamond } from '../utils/validators'
import { searchInventory } from '../utils/search'
import { uploadInventoryImages, deleteImage } from '../utils/image'

export const FETCH_DIAMONDS_REQUEST = 'FETCH_DIAMONDS_REQUEST'
function fetchDiamondsRequest() {
  return { type: FETCH_DIAMONDS_REQUEST }
}

export const FETCH_DIAMONDS_SUCCESS = 'FETCH_DIAMONDS_SUCCESS'
function fetchDiamondsSuccess(diamonds, callback) {
  return { type: FETCH_DIAMONDS_SUCCESS, diamonds, callback }
}

export const FETCH_DIAMONDS_ERROR = 'FETCH_DIAMONDS_ERROR'
function fetchDiamondsError(error) {
  return { type: FETCH_DIAMONDS_ERROR, error }
}

function fetchAllDiamonds(dispatch, getState, query, callback) {
  const pageNumber = query.start / query.length + 1
  let diamonds = getState().diamond.get('diamonds')
  const currentQuery = diamonds && diamonds.getMeta('query')
  if (
    !diamonds ||
    diamonds.getPageNumber() !== pageNumber ||
    (currentQuery && currentQuery !== query.search.value)
  ) {
    diamonds = api.getDiamonds(pageNumber)
  }
  return Promise.resolve(diamonds).then(
    diamonds => {
      dispatch(fetchDiamondsSuccess(diamonds, callback))
      return diamonds
    },
    error => {
      dispatch(fetchDiamondsError(error))
      throw error
    }
  )
}

/**
 * `options.marketplaceVisibleOnly` restricts the result to products a shopper
 * could actually reach — seller still exists, row still in the database, record
 * still in the Algolia index. Only the Laravel endpoint can answer that
 * (querying Algolia directly says nothing about whether the row was deleted or
 * its seller removed), so that mode stays on the API for search and filtering
 * too instead of switching over to Algolia.
 *
 * `options.liveSellerOnly` is the same seller check without the index check:
 * items whose seller is gone are dropped, but items that have drifted out of
 * Algolia are still listed so they can be managed. The inventory list uses it
 * so an orphaned product — blank Retailer column, no store to reach — never
 * shows up there.
 */
export function fetchDiamonds(query, callback, options = {}) {
  return (dispatch, getState) => {
    dispatch(fetchDiamondsRequest())
    const currentUser = getState().login.get('currentUser')
    let retailerId
    if (currentUser) {
      const _get = p => typeof currentUser.get === 'function' ? currentUser.get(p) : currentUser[p]
      if (_get('type') === 'retailer' && _get('retailerType') !== 'admin') {
        retailerId = _get('id')
      }
    }

    const pageNumber = query.start / query.length + 1
    const hasSearchText = !!(query.search && query.search.value && query.search.value.trim())
    const hasSettingFilter = !!(query.setting && query.setting !== 'loose')
    const hasJewelryTypeFilter = !!query.jewelryType

    if (options.marketplaceVisibleOnly) {
      return api
        .getDiamonds(pageNumber, {
          live_seller_only: 1,
          indexed_only: 1,
          // A non-admin seller only ever sees their own items, the same scoping
          // the Algolia path applies below.
          retailer_id: retailerId || undefined,
          search: hasSearchText ? query.search.value.trim() : undefined,
          // The dropdown value goes over as-is; the API routes it to the
          // setting or the jewelry_type column.
          setting: query.selectedFilter || undefined
        })
        .then(
          diamonds => {
            dispatch(fetchDiamondsSuccess(diamonds, callback))
          },
          error => {
            dispatch(fetchDiamondsError(error))
            if (typeof callback === 'function') callback(null)
          }
        )
    }

    // When the user is just browsing/paginating (no search text and no
    // setting/jewelryType filters), fetch from the Laravel API directly.
    // The Laravel /inventory endpoint sorts by created_at DESC, so the
    // latest inventory appears first — Algolia's default relevance sort
    // does not guarantee newest-first.
    if (!hasSearchText && !hasSettingFilter && !hasJewelryTypeFilter) {
      return api
        .getDiamonds(
          pageNumber,
          options.liveSellerOnly ? { live_seller_only: 1 } : undefined
        )
        .then(
          diamonds => {
            dispatch(fetchDiamondsSuccess(diamonds, callback))
          },
          error => {
            dispatch(fetchDiamondsError(error))
            if (typeof callback === 'function') callback(null)
          }
        )
    }

    // An Algolia hit carries no way to tell whether its seller still exists —
    // the index keeps records for deleted sellers — so searching and filtering
    // also go through the API when orphans have to stay hidden.
    if (options.liveSellerOnly) {
      return api
        .getDiamonds(pageNumber, {
          live_seller_only: 1,
          // A non-admin seller only ever sees their own items, the same scoping
          // the Algolia path applies below.
          retailer_id: retailerId || undefined,
          search: hasSearchText ? query.search.value.trim() : undefined,
          // The dropdown value goes over as-is; the API routes it to the
          // setting or the jewelry_type column.
          setting: query.selectedFilter || undefined
        })
        .then(
          diamonds => {
            dispatch(fetchDiamondsSuccess(diamonds, callback))
          },
          error => {
            dispatch(fetchDiamondsError(error))
            if (typeof callback === 'function') callback(null)
          }
        )
    }

    return searchInventory(
      query.search.value,
      query.start / query.length,
      retailerId,
      query.setting,
      query.jewelryType
    ).then(
      diamonds => {
        dispatch(fetchDiamondsSuccess(diamonds, callback))
      },
      error => {
        dispatch(fetchDiamondsError(error))
        if (typeof callback === 'function') callback(null)
      }
    )
  }
}

export const GET_DIAMOND_REQUEST = 'GET_DIAMOND_REQUEST'
function getDiamondRequest() {
  return { type: GET_DIAMOND_REQUEST }
}

export const GET_DIAMOND_SUCCESS = 'GET_DIAMOND_SUCCESS'
function getDiamondSuccess(diamond) {
  return { type: GET_DIAMOND_SUCCESS, diamond }
}

export const GET_DIAMOND_ERROR = 'GET_DIAMOND_ERROR'
function getDiamondError(error) {
  return { type: GET_DIAMOND_ERROR, error }
}

export function getDiamond(diamondId) {
  return (dispatch, getState) => {
    dispatch(getDiamondRequest())
    const diamonds = getState().diamond.get('diamonds')
    let diamond = diamonds ? diamonds.get(diamondId) : null
    if (!diamond || diamond.source === 'search') {
      diamond = api.getDiamond(diamondId)
    }
    return Promise.resolve(diamond).then(
      diamond => {
        dispatch(getDiamondSuccess(diamond))
      },
      error => {
        dispatch(getDiamondError(error))
      }
    )
  }
}

export const CREATE_DIAMOND = 'CREATE_DIAMOND'
export function createDiamond() {
  return { type: CREATE_DIAMOND }
}

export const SAVE_DIAMOND_REQUEST = 'SAVE_DIAMOND_REQUEST'
function saveDiamondRequest() {
  return { type: SAVE_DIAMOND_REQUEST }
}

export const SAVE_DIAMOND_SUCCESS = 'SAVE_DIAMOND_SUCCESS'
function saveDiamondSuccess(diamond) {
  return { type: SAVE_DIAMOND_SUCCESS, diamond }
}

export const SAVE_DIAMOND_ERROR = 'SAVE_DIAMOND_ERROR'
function saveDiamondError(error) {
  return { type: SAVE_DIAMOND_ERROR, error }
}

export const CLEAR_DIAMOND_FIELD_ERROR = 'CLEAR_DIAMOND_FIELD_ERROR'
export function clearDiamondFieldError(fieldName) {
  return { type: CLEAR_DIAMOND_FIELD_ERROR, fieldName }
}

export function saveDiamond(data, images) {
  return (dispatch, getState) => {
    const state = getState()
    const currentUser = state.login.get('currentUser')
    const { retailerId, saleMarkupType } = data.attributes
    if (!retailerId && currentUser) {
      const _get = p => typeof currentUser.get === 'function' ? currentUser.get(p) : currentUser[p]
      if (_get('type') === 'retailer' && _get('retailerType') !== 'admin') {
        data.attributes.retailerId = _get('id')
        data.attributes.retailerName = _get('name')
      }
    }
    if (saleMarkupType === 'none' || saleMarkupType === 'sale') {
      data.attributes.markup = ''
    }
    const selectedDiamond = state.diamond.get('selectedDiamond')
    const relatedImages = selectedDiamond.getRelated('images')
    if ((!relatedImages || relatedImages.size() < 1) &&
        data.attributes.firstImagePath) {
      data.attributes.firstImagePath = ''
    }
    const { valid, error } = validateDiamond(data.attributes, images)
    if (!valid) {
      error.type = 'validation'
      dispatch(saveDiamondError(error))
      return Promise.reject(error)
    }

    dispatch(saveDiamondRequest())
    const progress = (images, results) => {
      if (!images || images.length < 1) {
        dispatch(setFullscreenLoaderMessage('Please wait while we are saving the data...'))
        return
      }
      const uploaded = (results || []).length + 1
      const total = images.length
      const msg = `Uploading gallery images... (${uploaded}/${total})`
      dispatch(setFullscreenLoaderMessage(msg))
    }

    // Preserve URLs of images already uploaded (no new file selected)
    const existingImageUrls = images
      .filter(({ file, attributes }) => !file && attributes && attributes.url)
      .map(({ attributes }) => attributes.url)

    // Only upload images that have a newly selected file
    const newImages = images.filter(({ file }) => file)

    progress(newImages)
    return uploadInventoryImages(newImages.map(({ file }) => file), null, progress.bind(null, newImages))
      .then((newUrls) => {
        progress()
        const allImageUrls = [...existingImageUrls, ...newUrls.filter(url => url)]
        data.attributes.imageUrls = allImageUrls
        data.attributes.firstImagePath = allImageUrls[0] || '';
        return (data.id ? api.updateDiamond(data.id, data) : api.addDiamond(data))
      })
      .then(diamond => {
        dispatch(saveDiamondSuccess(diamond))
        return diamond
      })
      .catch(error => {
        //XXX: Error 504 is gateway timeout error from cloudflare, we
        //don't know the real status of our backend, so we consider
        //this as success.
        if (error.status === 504) {
          dispatch(saveDiamondSuccess())
          return
        }

        dispatch(saveDiamondError(error))
        throw error
      })
  }
}

export const DELETE_DIAMOND_REQUEST = 'DELETE_DIAMOND_REQUEST'
function deleteDiamondRequest() {
  return { type: DELETE_DIAMOND_REQUEST }
}

export const DELETE_DIAMOND_SUCCESS = 'DELETE_DIAMOND_SUCCESS'
function deleteDiamondSuccess() {
  return { type: DELETE_DIAMOND_SUCCESS }
}

export const DELETE_DIAMOND_ERROR = 'DELETE_DIAMOND_ERROR'
function deleteDiamondError(error) {
  return { type: DELETE_DIAMOND_ERROR, error }
}

export function deleteDiamond(diamond) {
  return dispatch => {
    dispatch(deleteDiamondRequest())
    return api.deleteDiamond(diamond.id).then(
      () => {
        dispatch(deleteDiamondSuccess())
      },
      error => {
        dispatch(deleteDiamondError(error))
        throw error
      }
    )
  }
}

export const DELETE_DIAMOND_IMAGE_SUCCESS = 'DELETE_DIAMOND_IMAGE_SUCCESS'
function deleteDiamondImageSuccess(image) {
  return { type: DELETE_DIAMOND_IMAGE_SUCCESS, image }
}

export function deleteDiamondImage(diamond, image) {
  return (dispatch, getState) => {
    dispatch(deleteImageRequest())
    return api.deleteDiamondImage(diamond.id, image).then(
      () => {
        const selectedDiamond = getState().diamond.get('selectedDiamond')
        const imageEntity = selectedDiamond.findIncluded(image)
        const imageUrl = imageEntity && imageEntity.attributes.url
        if (imageUrl) {
          deleteImage(imageUrl)
        }
        dispatch(deleteImageSuccess())
        dispatch(deleteDiamondImageSuccess(image))
      },
      error => {
        dispatch(deleteImageError(error))
        throw error
      }
    )
  }
}

export const CSV_UPLOAD_REQUEST = 'CSV_UPLOAD_REQUEST'
function csvUploadRequest() {
  return { type: CSV_UPLOAD_REQUEST }
}

export const CSV_UPLOAD_SUCCESS = 'CSV_UPLOAD_SUCCESS'
function csvUploadSuccess(result) {
  return { type: CSV_UPLOAD_SUCCESS, result }
}

export const CSV_UPLOAD_ERROR = 'CSV_UPLOAD_ERROR'
function csvUploadError(error) {
  return { type: CSV_UPLOAD_ERROR, error }
}

export function csvUpload({ file, retailerId }) {
  return (dispatch, getState) => {
    const currentUser = getState().login.get('currentUser')
    if (!retailerId && currentUser) {
      const _get = p => typeof currentUser.get === 'function' ? currentUser.get(p) : currentUser[p]
      if (_get('type') === 'retailer' && _get('retailerType') !== 'admin') {
        retailerId = _get('id')
      }
    }
    dispatch(csvUploadRequest())
    return api
      .csvUpload(retailerId, file)
      .then(
        result => {
          dispatch(csvUploadSuccess(result))
        },
        error => {
          dispatch(csvUploadError(error))
        }
      )
  }
}

export const CLEAR_CSV_UPLOAD_RESULT = 'CLEAR_CSV_UPLOAD_RESULT'
export const clearCSVUploadResult = () => ({ type: CLEAR_CSV_UPLOAD_RESULT })

export const CLEAR_DIAMONDS = 'CLEAR_DIAMONDS'
export const clearDiamonds = () => ({ type: CLEAR_DIAMONDS })

export const CLEAR_SELECTED_DIAMOND = 'CLEAR_SELECTED_DIAMOND'
export const clearSelectedDiamond = () => ({ type: CLEAR_SELECTED_DIAMOND })


